import { KeyboardEvent, memo, useContext, useEffect, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { useHistory } from 'react-router-dom'
import { tokens } from '@getgo/chameleon-core/next/goto-admin'
import { CloseOutlinedIcon, SearchOutlinedIcon } from '@getgo/chameleon-icons/react'
import { IconButton, Link } from '@getgo/chameleon-web-react-wrapper'
import { Grid, makeStyles, Paper } from '@material-ui/core'
import TextField from '@material-ui/core/TextField'
import { Autocomplete, createFilterOptions, FilterOptionsState } from '@material-ui/lab'
import useLocalStorage from '@rehooks/local-storage'
import mixpanel from 'mixpanel-browser'

import { UnleashContext } from 'components/providers/UnleashProvider'
import config from 'config'
import { GlobalSearchBarDetails } from 'types/GlobalSearchBarDetails'
import { mixpanelEventNames } from 'utils'
import NoResultOption from './NoResultOption'
import OptionLink from './OptionLink'
import useExtractSearchDetails, { FrequentSearch } from './useExtractSearchDetails'

const SEARCH_BAR_MAX_LENGTH = 30

const useStyles = makeStyles(() => ({
  field: {
    width: 380,
    [`& .${config.CSS_CLASSNAMES_PREFIX}-MuiBox-root`]: {
      marginBottom: 0,
    },
  },
  option: {
    paddingLeft: '0px !important',
    padding: 0,
    '&[data-focus="true"]': {
      backgroundColor: tokens.surfaceColorSecondary,
    },
    '&[aria-selected="true"]': {
      backgroundColor: tokens.surfaceColorSecondary,
    },
    '&:active': {
      backgroundColor: tokens.surfaceColorSecondary,
    },
  },
  input: { borderColor: `${tokens.surfaceColorInverted} !important` },
  groupLabel: {
    fontSize: 12,
  },
}))

const filterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option: GlobalSearchBarDetails) => option.keyword + option.tags?.join(' '),
})

const trackMixPanelEvent = (eventName: string, payload = {}, searchTerm?: string) => {
  mixpanel.track(`${mixpanelEventNames.GLOBAL_SEARCH} > ${eventName}`, {
    ...(searchTerm && searchTerm.length >= 4 && { searchTerm }),
    ...(searchTerm && { searchSplit: [...new Set(searchTerm.split(' ').filter((item) => item))] }),
    ...payload,
  })
}

interface GlobalSearchBarProps {
  openAdminCopilot: () => void
}

const GlobalSearchBar = ({ openAdminCopilot }: GlobalSearchBarProps): JSX.Element => {
  const classes = useStyles()
  const t = useTranslate()
  const history = useHistory()
  const { copilotAIEnabled } = useContext(UnleashContext)

  const [keyword, setKeyword] = useState('')
  const [searchOptions, setSearchOptions] = useState<GlobalSearchBarDetails[]>([])

  const [frequentSearches, setFrequentSearches] = useLocalStorage<FrequentSearch>(
    config.GLOBAL_SEARCH_FREQUENT_SEARCHES,
    {},
  )

  const { globalKeywords, topFiveFrequentlySearched } = useExtractSearchDetails({ frequentSearches })

  const onClickOption = (option: GlobalSearchBarDetails) => {
    trackMixPanelEvent('Result clicked', { resultDetails: option }, keyword)

    const newCount = frequentSearches[option.keyword] ? frequentSearches[option.keyword] + 1 : 1

    const sortedFrequentlySearch = Object.fromEntries(
      Object.entries({ ...frequentSearches, [option.keyword]: newCount }).sort(([, a], [, b]) => b - a),
    )
    setFrequentSearches(sortedFrequentlySearch)
  }

  useEffect(() => {
    setSearchOptions(keyword.length === 0 ? topFiveFrequentlySearched : globalKeywords)
  }, [globalKeywords, keyword, topFiveFrequentlySearched])

  return (
    <>
      {searchOptions.length > 0 && (
        <Grid item id="global-search-bar">
          <Autocomplete
            autoComplete
            className={classes.field}
            classes={{
              option: classes.option,
              groupLabel: classes.groupLabel,
            }}
            clearOnEscape
            groupBy={(option) => option.group || ''}
            closeIcon={<CloseOutlinedIcon />}
            disableClearable
            getOptionLabel={(option: GlobalSearchBarDetails) => option.keyword}
            inputValue={keyword}
            filterOptions={(options: GlobalSearchBarDetails[], state: FilterOptionsState<GlobalSearchBarDetails>) => {
              const results = filterOptions(options, state)
              if (keyword.length >= 3) {
                trackMixPanelEvent('Search changed', { hasResults: !!results.length }, keyword)
              }
              return results
            }}
            noOptionsText={<NoResultOption keyword={keyword} />}
            options={searchOptions}
            onInputChange={(_event, newInputValue, reason) => {
              if (reason === 'reset') {
                setKeyword('')
              } else if (newInputValue.length < SEARCH_BAR_MAX_LENGTH) {
                setKeyword(newInputValue)
              }
            }}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
              // @ts-expect-error:next-line
              const value = e.target.value

              if (e.key === 'Enter' && value) {
                const foundOption = searchOptions.find((option) => option.keyword === value)
                if (foundOption) {
                  trackMixPanelEvent('Result clicked', { resultDetails: foundOption }, keyword)
                  foundOption.external ? window.open(foundOption.to, '_blank') : history.push(foundOption.to)
                }
              }
            }}
            renderOption={(option) => <OptionLink option={option} onClickOption={onClickOption} />}
            onOpen={() => trackMixPanelEvent('Opened')}
            renderInput={(props) => (
              <TextField
                {...props}
                placeholder={t('What are you looking for?')}
                variant="outlined"
                size="small"
                InputProps={{
                  ...props.InputProps,
                  classes: {
                    notchedOutline: classes.input,
                  },
                  autoComplete: 'off',
                  startAdornment: <SearchOutlinedIcon />,
                  endAdornment: keyword && (
                    <IconButton size="small" data-testid="clear-search-input" onClick={() => setKeyword('')}>
                      <CloseOutlinedIcon />
                    </IconButton>
                  ),
                }}
                inputProps={{
                  ...props.inputProps,
                  'data-lpignore': true,
                  'data-testid': 'search-input',
                }}
              />
            )}
            PaperComponent={({ children }) => (
              <Paper>
                {copilotAIEnabled ? (
                  <>
                    <Link
                      onMouseDown={openAdminCopilot}
                      style={{
                        display: 'flex',
                        justifyContent: 'end',
                        marginBottom: '-12px',
                        padding: '8px 16px 0px',
                      }}
                    >
                      {t('Take me to GoPilot')}
                    </Link>
                    {children}
                  </>
                ) : (
                  <>{children}</>
                )}
              </Paper>
            )}
          />
        </Grid>
      )}
    </>
  )
}

export default memo(GlobalSearchBar)
